/** Pure state transitions for the segmented 6-digit code input. The DOM
 * component (components/codeInput.tsx) owns the <input>s and focus; this owns
 * the "what should the cells + focus become" decisions so they can be tested
 * without a DOM. */

export const CODE_LENGTH = 6;

/** Distribute `raw` (a typed char, a pasted string, or an autofilled code)
 * across the cells, keeping digits only. A whole code starts at the first cell
 * wherever it landed — iOS autofills the focused cell, not the first — while a
 * shorter run continues from `index`. Returns the cells and the cell to focus. */
export function fillFrom(
  cells: string[],
  index: number,
  raw: string,
): { cells: string[]; focus: number } {
  const digits = raw.replace(/\D/g, "");
  const next = cells.slice();
  let i = digits.length >= CODE_LENGTH ? 0 : index;
  for (const d of digits) {
    if (i >= CODE_LENGTH) break;
    next[i] = d;
    i += 1;
  }
  return { cells: next, focus: Math.min(i, CODE_LENGTH - 1) };
}

/** Backspace behaviour: clear the current cell if it holds a digit, otherwise
 * clear the previous cell and move focus back to it. */
export function backspace(
  cells: string[],
  index: number,
): { cells: string[]; focus: number } {
  const next = cells.slice();
  if (next[index]) {
    next[index] = "";
    return { cells: next, focus: index };
  }
  const prev = Math.max(0, index - 1);
  next[prev] = "";
  return { cells: next, focus: prev };
}

export function code(cells: string[]): string {
  return cells.join("");
}

export function isComplete(cells: string[]): boolean {
  return cells.length === CODE_LENGTH && cells.every((c) => c !== "");
}
